import { useEffect, useState } from 'react';
import {
  adoptRefreshTarget,
  hardRefresh,
  isRefreshHeld,
  onRefreshReleased,
  type RefreshGate,
} from '../lib/appRefresh';

/**
 * Drives the remote-update refresh gate. Renders nothing for silent/adopt modes,
 * a non-blocking banner for Delay updates, and a blocking overlay only for
 * store-update + the loop-guard fail-safe. See lib/appRefresh.ts.
 */
export function RefreshController({ gate }: { gate: RefreshGate | null }) {
  const mode = gate?.mode ?? null;
  const target = gate && 'target' in gate ? gate.target : null;

  // First run: silently adopt the current token, show nothing ever.
  useEffect(() => {
    if (gate?.mode === 'adopt') adoptRefreshTarget(gate.target);
  }, [mode, target]); // eslint-disable-line react-hooks/exhaustive-deps

  // Instant Update: hard-refresh silently, but wait out any in-flight hold
  // (a live game / upload) so we never interrupt.
  useEffect(() => {
    if (gate?.mode !== 'silent') return;
    const t = gate.target;
    if (!isRefreshHeld()) { void hardRefresh(t); return; }
    const off = onRefreshReleased(() => { off(); void hardRefresh(t); });
    return off;
  }, [mode, target]); // eslint-disable-line react-hooks/exhaustive-deps

  if (gate?.mode === 'banner') return <UpdateBanner key={gate.target} gate={gate} />;
  if (gate?.mode === 'overlay' || gate?.mode === 'store-update') return <UpdateOverlay gate={gate} />;
  return null;
}

/** Delay Update: non-blocking top banner; the app stays fully usable underneath. */
function UpdateBanner({ gate }: { gate: Extract<RefreshGate, { mode: 'banner' }> }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className="fixed inset-x-0 top-0 z-[1000] flex items-center gap-3 bg-squishy-tealDark px-4 py-2.5 text-white shadow-lg" style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top))' }}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{gate.title}</p>
        {gate.message && <p className="truncate text-xs text-white/80">{gate.message}</p>}
      </div>
      <button
        onClick={() => void hardRefresh(gate.target)}
        className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-squishy-tealDark active:scale-95"
      >
        Reload
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="shrink-0 rounded-lg px-2 py-1.5 text-xs font-medium text-white/70 active:scale-95"
      >
        Later
      </button>
    </div>
  );
}

/** Blocking overlay: only for store-update, or the loop-guard fail-safe. */
function UpdateOverlay({ gate }: { gate: Extract<RefreshGate, { mode: 'store-update' | 'overlay' }> }) {
  const isStore = gate.mode === 'store-update';
  const handleClick = () => {
    if (isStore) {
      if (gate.updateUrl) window.location.href = gate.updateUrl;
      return;
    }
    void hardRefresh(gate.target);
  };
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 text-center shadow-2xl">
        <img src="/logo.png" alt="" className="mx-auto h-14 w-14" />
        <h1 className="mt-3 text-lg font-bold text-slate-900">{gate.title}</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600 whitespace-pre-wrap">{gate.message}</p>
        <button
          onClick={handleClick}
          className="mt-5 w-full rounded-xl bg-squishy-tealDark px-4 py-3 text-sm font-semibold text-white shadow-sm active:scale-[0.99]"
        >
          {isStore ? 'Update App' : 'Restart App'}
        </button>
      </div>
    </div>
  );
}
